import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Models as ModelsService } from '../../../services/Models'
import { AutomatModel as ModelType, AutomatModelTypes } from '../../../types'
import { Layout } from '../../complexes/Layout'
import { Header } from '../../simples/tablePages/Header'
import { TopPanel } from '../../simples/tablePages/TopPanel'
import { ItemsList } from '../../simples/tablePages/ItemsList'
import { ModelEditor } from '../../complexes/ModelEditor'
import { Confirm } from '../../simples/Confirm'

const Model: FC = () => {
    const { t } = useTranslation()
    const header = [t('388_Model_modelName'), t('389_Model_automateType')]

    const [models, setModels] = useState<Array<ModelType>>([])
    const modelCurrent = useRef<Array<ModelType>>(models)
    modelCurrent.current = models
    const [loading, setLoading] = useState(false)
    const offset = useRef(0)
    const search = useRef('')
    const has = useRef(true)

    const [showModelEditor, setShowModelEditor] = useState(false)
    const [editedModel, setEditedModel] = useState<ModelType>()

    const [deletedModelId, setDeletedModelId] = useState(0)

    const load = async () => {
        if (!has.current || loading) {
            return
        }

        setLoading(true)
        const result = await ModelsService.getModels({
            offset: offset.current,
            search: search.current,
        })
        setLoading(false)

        if (!result.length) {
            has.current = false
            return
        }

        offset.current = offset.current + result.length
        setModels([...modelCurrent.current, ...result])
    }

    const clear = () => {
        has.current = true
        offset.current = 0
        search.current = ''
        setModels([])
    }

    useEffect(() => {
        load().then()
    }, [])

    const handleEndReached = async () => {
        await load()
    }

    const handleSearch = async (text: string) => {
        clear()
        search.current = text
        await load()
    }

    const startCreateModel = () => {
        setShowModelEditor(true)
    }

    const startUpdateModel = (modelId: number) => {
        const model = models.find((b) => b.id === modelId)

        if (!model) {
            return
        }

        setShowModelEditor(true)
        setEditedModel(model)
    }

    const handleSubmit = async (savedModel: ModelType) => {
        let updated = false

        let newModels = models.map((b) => {
            if (b.id === savedModel.id) {
                updated = true
                return savedModel
            }

            return b
        })

        if (!updated) {
            newModels = [savedModel, ...newModels]
        }

        setModels(newModels)

        closeModelEditor()
    }

    const closeModelEditor = () => {
        setShowModelEditor(false)
        setEditedModel(undefined)
    }

    const startDeleteModel = (modelId: number) => {
        setDeletedModelId(modelId)
    }

    const cancelModelDelete = () => {
        setDeletedModelId(0)
    }

    const confirmModelDelete = async () => {
        setDeletedModelId(0)
        await ModelsService.delete(deletedModelId)

        setModels(models.filter((model) => model.id !== deletedModelId))
    }

    const rows = useMemo(() => {
        return models.map((model) => {
            let type = ''
            switch (model.type) {
                case AutomatModelTypes.Beverages:
                    type = t('390_Model_beverages')
                    break

                case AutomatModelTypes.Snack:
                    type = t('391_Model_snacks')
                    break

                case AutomatModelTypes.Coffee:
                    type = t('392_Model_coffee')
                    break

                case AutomatModelTypes.Kiosk:
                    type = t('634_Model_kiosk')
            }

            return {
                id: model.id,
                values: [model.name, type],
            }
        })
    }, [models])

    return (
        <Layout onEndReached={handleEndReached}>
            <Header text={t('393_Model_baseCreation')} />
            <TopPanel
                createButtonName={t('394_Model_create')}
                onCreateButtonClick={startCreateModel}
                onSearch={handleSearch}
            />
            <ItemsList
                headers={header}
                rows={rows}
                loading={loading}
                onEdit={startUpdateModel}
                onDelete={startDeleteModel}
            />

            {showModelEditor && (
                <ModelEditor
                    model={editedModel}
                    onSubmit={handleSubmit}
                    onClose={closeModelEditor}
                />
            )}

            {!!deletedModelId && (
                <Confirm
                    text={t('395_Model_confirmDelete')}
                    onConfirm={confirmModelDelete}
                    onCancel={cancelModelDelete}
                />
            )}
        </Layout>
    )
}

export default Model
